import {
  Button,
  CrudMode,
  UpdateIconButton,
  formatDate,
  formatDecimal,
  formatPercent,
  ListContainer,
  ListContext,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  pn,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@compass/core";
import { ListBeProperty } from "components";
import {
  PartnerIndirectCost,
  PartnerIndirectCostListDto,
  PartnerIndirectCostListFilter,
  PartnerIndirectCostListResource,
  Permission,
  PermissionAction,
} from "generated/openApi";
import { useList, useRouter } from "hooks";
import "moment-timezone";
import { partnerIndirectCostPageRoute } from "pages/match-utility/partner-indirect-costs/PartnerIndirectCostPage/PartnerIndirectCostPage";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const IndirectCosts = (props: { initialLoad: boolean; partnerId: number; onGetList: () => void }) => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();

  const [listFilter, setListFilter] = useState<PartnerIndirectCostListFilter>({
    indirectCostFilter: { partnerId: props.partnerId },
    page: PAGE_DEFAULT,
    pageSize: PAGE_SIZE_UNLIMITED,
    sortObjects: [{ property: pn<PartnerIndirectCostListDto>("startDate"), isDesc: true }],
  });
  const [listItems, setListItems] = useState<PartnerIndirectCostListDto[]>([]);
  const [resource, setResource] = useState<PartnerIndirectCostListResource>({});
  const properties = resource.properties || [];

  const { tableContainerRef, loadList, applySort } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.partnerIndirectCosts.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
  });

  useEffect(() => {
    if (props.initialLoad) {
      loadList(listFilter);

      ApiService.partnerIndirectCosts.getListResource().then((response) => {
        setResource(response);
      });
    }
  }, [props.initialLoad]);

  const goToIndirectCostPage = (id: number, crudMode: CrudMode) => {
    navigate({
      pathname: partnerIndirectCostPageRoute.getPath({ id: id, crudMode: crudMode, screenType: "dialog" }),
      search: partnerIndirectCostPageRoute.getOptionalParams?.({
        partnerId: props.partnerId,
      }),
    });
  };

  return (
    <>
      <ListContainer
        fullHeight
        header={
          <Stack direction="row" justifyContent="flex-end">
            {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
              <Button onClick={() => goToIndirectCostPage(0, "edit")} variant="outlined">
                + New
              </Button>
            )}
          </Stack>
        }
        tableContainer={
          <TableContainer ref={tableContainerRef}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties?.find((x) => x.propertyName === pn<PartnerIndirectCost>("startDate"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties?.find((x) => x.propertyName === pn<PartnerIndirectCost>("endDate"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <ListBeProperty
                      listProperty={properties?.find(
                        (x) => x.propertyName === pn<PartnerIndirectCost>("indirectPercent")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <ListBeProperty
                      listProperty={properties?.find((x) => x.propertyName === pn<PartnerIndirectCost>("mileage"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listItems.map((listItem) => (
                  <TableRow key={listItem.id} onClick={() => goToIndirectCostPage(listItem.id, "view")}>
                    <TableCell>{formatDate(listItem.startDate)}</TableCell>
                    <TableCell>{formatDate(listItem.endDate)}</TableCell>
                    <TableCell align="right">{formatPercent(listItem.indirectPercent)}</TableCell>
                    <TableCell align="right">{formatDecimal(listItem.mileage, 3)}</TableCell>
                    <TableCell type="action">
                      {isAuthorized(ROOT_PERMISSION, PermissionAction.Update) && (
                        <UpdateIconButton onClick={() => goToIndirectCostPage(listItem.id, "edit")} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        }
      />

      <ListContext.Provider
        value={{
          loadList: (options) => {
            loadList(listFilter, { resetPage: options?.resetPage });
            props.onGetList();
          },
        }}
      >
        {/* Outlet will only render for the designated route to prevent conflicts with others on parent Page */}
        {matchTemplatePath(partnerIndirectCostPageRoute.templatePath) && <Outlet />}
      </ListContext.Provider>
    </>
  );
};
